/**
 * Reusable Promo Code Utility Functions
 * Used across all insurance types (Travel, Home, Personal Cyber, etc.)
 */

import Axios from "../redux/axiosInstance";
import { policyApiEndpoints } from "../api";
import { getClientSource } from "./getClientSource";

/**
 * Apply promo code to a quote
 * @param {string} quoteNumber - The quote/policy number
 * @param {string} promoCode - The promo code to apply
 * @param {Function} translate - Translation function
 * @returns {Promise<Object>} - Result object with success status and data/error
 */
export const applyPromoCodeToQuote = async (quoteNumber, promoCode, translate) => {
  if (!promoCode || !promoCode.trim()) {
    return {
      success: false,
      error: translate("Please enter a promo code", "يرجى إدخال كود الخصم"),
    };
  }

  if (!quoteNumber || quoteNumber === "N/A") {
    return {
      success: false,
      error: translate(
        "Quote number is missing. Please refresh and try again.",
        "رقم العرض مفقود. يرجى تحديث الصفحة والمحاولة مرة أخرى."
      ),
    };
  }

  try {
    const response = await Axios.post(policyApiEndpoints.applyPromo, {
      QuoteNumber: quoteNumber,
      PromoCode: promoCode.trim(),
      Channel: getClientSource(),
    });
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: translate(
        "Invalid Code! Please enter a valid Promo Code and try again.",
        "كود غير صالح! يرجى إدخال كود خصم صالح والمحاولة مرة أخرى."
      ),
    };
  }
};

/**
 * Calculate discount amount from original and new premium
 * @param {number} originalPremium - Original total premium before promo
 * @param {number} newPremium - New total premium after promo
 * @returns {number} - Discount amount
 */
export const calculatePromoDiscount = (originalPremium, newPremium) => {
  return originalPremium - newPremium;
};

/**
 * Get premium from promo API response
 * @param {Object} promoAppliedQuoteData - Data from promo API response
 * @returns {number} - Premium value from API
 */
export const getPremiumFromPromoResponse = (promoAppliedQuoteData) => {
  return promoAppliedQuoteData?.Premium || 0;
};

/**
 * Get tax from promo API response
 * @param {Object} promoAppliedQuoteData - Data from promo API response
 * @returns {number} - Tax value from API
 */
export const getTaxFromPromoResponse = (promoAppliedQuoteData) => {
  return promoAppliedQuoteData?.Tax || 0;
};

/**
 * Get total premium from promo API response
 * @param {Object} promoAppliedQuoteData - Data from promo API response
 * @returns {number} - Total premium value from API
 */
export const getTotalPremiumFromPromoResponse = (promoAppliedQuoteData) => {
  return promoAppliedQuoteData?.TotalPremium || 0;
};

/**
 * Get final quote number for payment
 * If promo is applied, use the PolicyNumber from promo API response
 * Otherwise use the original quote number
 * @param {boolean} promoCodeApplied - Whether promo code was applied
 * @param {Object} promoAppliedQuoteData - Data from promo API response
 * @param {string} originalQuoteNumber - Original quote number
 * @returns {string} - Final quote number to use for payment
 */
export const getFinalQuoteNumberForPayment = (
  promoCodeApplied,
  promoAppliedQuoteData,
  originalQuoteNumber
) => {
  return promoCodeApplied && promoAppliedQuoteData
    ? promoAppliedQuoteData.PolicyNumber
    : originalQuoteNumber;
};

/**
 * Get final premium for payment
 * If promo is applied, use the TotalPremium from promo API response
 * Otherwise use the original total premium
 * @param {boolean} promoCodeApplied - Whether promo code was applied
 * @param {Object} promoAppliedQuoteData - Data from promo API response
 * @param {number} originalTotalPremium - Original total premium
 * @returns {number} - Final premium to use for payment
 */
export const getFinalPremiumForPayment = (
  promoCodeApplied,
  promoAppliedQuoteData,
  originalTotalPremium
) => {
  return promoCodeApplied && promoAppliedQuoteData
    ? promoAppliedQuoteData.TotalPremium
    : originalTotalPremium;
};
